import { ApolloError, ApolloQueryResult, useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from 'luxon';
import { always, cond, equals, map } from 'ramda';
import { useContext } from 'react';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { billTypes } from '@data/bills';

import billsQuery from '@graphql/queries/bills.query.gql';

import {
	IInvoiceApi,
	IInvoiceLineItem,
	InvoiceStatus,
	PaymentStatus,
	RefundStatus,
} from './useMemberInvoice';
import { IMemberInvoiceProposalLineItem } from './useMemberInvoiceProposal';
import { doesNotExist } from '@utils/general';

type IAuthenticatedMemberBill =
	| ({ __typename: 'Invoice' } & IInvoiceApi)
	| ({ __typename: 'InvoiceProposal' } & IMemberInvoiceProposal);

export type IMemberBill =
	| ({ __typename: 'Invoice' } & IFormattedInvoice)
	| ({ __typename: 'InvoiceProposal' } & IFormattedInvoiceProposal);

export type IFormattedInvoiceProposal =
	| {
			proposalId: string;
			proposalStatus: string;
			total: number;
			created: DateTime;
			dueDate: DateTime | null;
			kind: null;
			invoice?: {
				invoiceId: string;
				status: InvoiceStatus;
				paymentStatus: PaymentStatus;
				refundStatus: RefundStatus | null;
				created: DateTime;
				dueDate: DateTime | null;
				paidDate: DateTime | null;
				total: number;
				amountPaid: number;
				amountRemaining: number;
				kind: keyof typeof billTypes;
			};
			invoiceId: never;
			status: never;
			paymentStatus: never;
			refundStatus: never;
	  }
	| {
			proposalId: string;
			proposalStatus: string;
			total: number;
			created: DateTime;
			dueDate: DateTime | null;
			kind: keyof typeof billTypes;
			invoice: null;
			invoiceId: never;
			status: never;
			paymentStatus: never;
			refundStatus: never;
	  };

interface IFormattedInvoice {
	invoiceId: string;
	status: InvoiceStatus;
	number: string;
	created: DateTime;
	dueDate: DateTime | null;
	paidDate: DateTime | null;
	kind: keyof typeof billTypes;
	total: number;
	amountPaid: number;
	amountRemaining: number;
	paymentStatus: PaymentStatus;
	refundStatus: RefundStatus | null;
	hostedInvoiceUrl?: string | null;
	paymentMethod: {
		card: {
			brand: string;
			last4: string;
		};
	} | null;
	lineItems: IInvoiceLineItem[];
	invoice: never;
	proposalId: never;
	proposalStatus: never;
}

interface IMemberInvoiceProposal {
	invoice?: {
		total: number;
		amountPaid: number;
		amountRemaining: number;
		created: string;
		dueDate: string | null;
		paidDate: string | null;
		paymentMethod: {
			card: {
				brand: string;
				last4: string;
			};
		};
		kind: keyof typeof billTypes;
		refundStatus: PaymentStatus;
	};
	id: string;
	creationDate: string;
	dueDate: string | null;
	proposalStatus: string;
	total: number;
	lineItems: IMemberInvoiceProposalLineItem[];
}

interface IAuthenticatedMemberBillQueryResult {
	authenticatedMember: {
		bills: {
			nodes: IAuthenticatedMemberBill[];
			firstBillDate?: string;
			lastBillDate?: string;
		};
	};
}

type UseMemberBills = (
	startDate: string,
	endDate: string
) => {
	loading: boolean;
	data: { bills: IMemberBill[]; firstBillDate?: string; lastBillDate?: string };
	error?: ApolloError;
	refetch: (
		startDate: string,
		endDate: string
	) => Promise<ApolloQueryResult<IAuthenticatedMemberBillQueryResult>>;
};

const useMemberBills: UseMemberBills = (startDate, endDate) => {
	const { loggedIn } = useAuth();
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { loading, data, error, refetch } = useQuery<IAuthenticatedMemberBillQueryResult>(
		billsQuery,
		{
			variables: {
				startDate,
				endDate,
			},
			client: graphQLv2Client,
			skip: !loggedIn || doesNotExist(startDate),
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all',
		}
	);

	const bills = data?.authenticatedMember?.bills?.nodes ?? [];
	const firstBillDate = data?.authenticatedMember?.bills?.firstBillDate;
	const lastBillDate = data?.authenticatedMember?.bills?.lastBillDate;
	const formattedBills = map((bill) => {
		if (bill.__typename === 'Invoice') {
			return {
				...bill,
				created: DateTime.fromISO(bill.created),
				dueDate: bill?.dueDate ? DateTime.fromISO(bill.dueDate) : null,
				paidDate: bill?.paidDate ? DateTime.fromISO(bill.paidDate) : null,
				refundStatus: equals(PaymentStatus.succeeded, bill?.refundStatus)
					? RefundStatus.refunded
					: null,
			};
		}
		if (bill.__typename === 'InvoiceProposal') {
			const invoice = bill?.invoice
				? {
						...bill?.invoice,
						created: DateTime.fromISO(bill.invoice.created),
						dueDate: bill?.invoice?.dueDate ? DateTime.fromISO(bill.invoice.dueDate) : null,
						paidDate: bill?.invoice?.paidDate ? DateTime.fromISO(bill.invoice.paidDate) : null,
						refundStatus: equals(PaymentStatus.succeeded, bill?.invoice?.refundStatus)
							? RefundStatus.refunded
							: null,
				  }
				: null;

			const status = cond([
				[equals('OPEN'), always(InvoiceStatus.open)],
				[equals('PAID'), always(InvoiceStatus.paid)],
				[equals('VOID'), always(InvoiceStatus.void)],
				[equals('UNCOLLECTIBLE'), always(InvoiceStatus.uncollectible)],
			])(bill?.proposalStatus);

			return {
				...bill,
				created: DateTime.fromISO(bill.creationDate),
				dueDate: bill?.dueDate ? DateTime.fromISO(bill?.dueDate) : null,
				kind: invoice ? 'medications' : null,
				invoice,
				status,
			};
		}
		// Error: code should never reach here
		datadogRum.addError('Unresolved bill type found: ', bill);
		return bill;
	}, bills);

	return {
		loading,
		data: { bills: formattedBills, firstBillDate, lastBillDate },
		error,
		refetch,
	};
};
export default useMemberBills;
